import { createServerFn } from "@tanstack/react-start";

export interface ContactEnquiryInput {
  name: string;
  email: string;
  notes: string;
  /** Honeypot field — real people leave it empty, bots fill it in. */
  website?: string;
  /** Milliseconds the visitor spent on the form before submitting. */
  elapsedMs?: number;
  /** Optional assessment results PDF, base64 encoded, attached to the email. */
  assessmentPdfBase64?: string;
}

/** Roughly 4 MB of base64 — plenty for the results PDF, small enough for SES. */
const MAX_PDF_BASE64 = 4_000_000;

/** Bots submit near-instantly; a human needs a few seconds to type. */
const MIN_FILL_MS = 3000;

function clean(value: unknown, max: number): string {
  return typeof value === "string" ? value.trim().slice(0, max) : "";
}

/** Sends a contact form message to the support inbox. */
export const submitContactEnquiry = createServerFn({ method: "POST" })
  .inputValidator((input: ContactEnquiryInput) => {
    const name = clean(input?.name, 200);
    const email = clean(input?.email, 320);
    const notes = clean(input?.notes, 5000);
    const website = clean(input?.website, 200);
    const elapsedMs = typeof input?.elapsedMs === "number" ? input.elapsedMs : 0;
    if (!name || !email || !notes) throw new Error("Please fill in every field.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) throw new Error("Please enter a valid email.");
    // Spam signals: honeypot filled, or the form completed faster than a person could type.
    if (website || elapsedMs < MIN_FILL_MS) throw new Error("spam_suspected");
    if (/https?:\/\/[^\s]+/gi.test(notes) && (notes.match(/https?:\/\//gi)?.length ?? 0) > 2) {
      throw new Error("spam_suspected");
    }
    // Accept the results PDF only when it really is base64-encoded PDF data.
    const raw =
      typeof input?.assessmentPdfBase64 === "string" ? input.assessmentPdfBase64.trim() : "";
    const assessmentPdfBase64 =
      raw &&
      raw.length <= MAX_PDF_BASE64 &&
      /^[A-Za-z0-9+/=\s]+$/.test(raw) &&
      raw.startsWith("JVB")
        ? raw
        : "";
    return { name, email, notes, assessmentPdfBase64 };
  })
  .handler(async ({ data }) => {
    const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
    try {
      await sendTemplateEmail("contact-enquiry", "", {
        templateData: {
          fullName: data.name,
          email: data.email,
          notes: data.notes,
          hasAssessmentPdf: Boolean(data.assessmentPdfBase64),
        },
        replyTo: data.email,
        attachments: data.assessmentPdfBase64
          ? [
              {
                filename: "leadership-gaps-assessment-results.pdf",
                contentBase64: data.assessmentPdfBase64,
                contentType: "application/pdf",
              },
            ]
          : undefined,
      });
      return { sent: true as const };
    } catch (error) {
      // Log server-side only; never surface provider details to the visitor.
      console.error("[contact] failed to send enquiry", error);
      throw new Error("We couldn't send your message just now. Please try again.");
    }
  });
